import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cleanTrail } from '../../utils/trailClean';

const API_BASE = '/planner-api';

// ── Simplificação da geometria da rota (Douglas-Peucker) ──────────────────
// A geometria do provider (Google/HERE) vem super-detalhada: uma rota de ~1000
// km pode ter >600 KB de pontos, e isso era o grande peso ao GRAVAR (upload) e
// ao CARREGAR (download) — crítico em redes lentas (Angola). Aqui removemos
// pontos redundantes em troços rectos, mantendo a FORMA da linha. É só a linha
// PLANEADA; a distância/duração vêm de computedRoute.distance (número do
// provider), NÃO são recalculadas da geometria, por isso NÃO mudam. Tolerância
// conservadora (~10 m): impercetível na vista normal.
const SIMPLIFY_TOLERANCE_DEG = 0.00009; // ≈ 10 m perto do equador
function sqSegDist(p, a, b) {
  let x = a[0], y = a[1], dx = b[0] - x, dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) { x = b[0]; y = b[1]; } else if (t > 0) { x += dx * t; y += dy * t; }
  }
  dx = p[0] - x; dy = p[1] - y;
  return dx * dx + dy * dy;
}
function simplifyCoords(coords, tol = SIMPLIFY_TOLERANCE_DEG) {
  if (!Array.isArray(coords) || coords.length < 3) return coords;
  const n = coords.length, sqTol = tol * tol;
  const keep = new Uint8Array(n);
  keep[0] = 1; keep[n - 1] = 1;
  const stack = [[0, n - 1]];
  while (stack.length) {
    const [first, last] = stack.pop();
    let maxSq = 0, idx = -1;
    for (let i = first + 1; i < last; i++) {
      const sq = sqSegDist(coords[i], coords[first], coords[last]);
      if (sq > maxSq) { maxSq = sq; idx = i; }
    }
    if (maxSq > sqTol && idx !== -1) { keep[idx] = 1; stack.push([first, idx], [idx, last]); }
  }
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(coords[i]);
  return out;
}
// Devolve uma cópia de computedRoute com a geometria simplificada (só quando
// vale a pena). NÃO muta o objecto do store.
function simplifyComputedRoute(cr) {
  const coords = cr?.geometry?.coordinates;
  if (cr?.geometry?.type !== 'LineString' || !Array.isArray(coords) || coords.length <= 500) return cr;
  const simplified = simplifyCoords(coords);
  if (simplified.length >= coords.length) return cr;
  return { ...cr, geometry: { ...cr.geometry, coordinates: simplified } };
}

const emptyRoute = {
  id: null,
  name: '',
  origin: null,       // { name, coordinates: [lng, lat] }
  destination: null,  // { name, coordinates: [lng, lat] }
  waypoints: [],      // [{ name, coordinates: [lng, lat] }]
  computedRoute: null,// { geometry, distance, duration, legs }
  stops: [],          // [{ km, drivingMin, arrivalTime, duration, type }]
  status: 'idle',     // idle | calculating | ready | error
  vehicleId: '',
  driverId: '',
  cargoRef: '',
  departureTime: null,
  arrivalTimeWindow: null, // { start: ISO, end: ISO }
  roundTrip: false,
  routeMarkers: [],       // [{ id, type, name, coordinates, notes }] type: safe|overnight|poi|fuel
  errorMessage: '',
  warning: '',
  fuelType: 'diesel',
  fuelConsumption: null,
  fuelPrice: null,
  routeOptions: {
    avoidTolls: false,
    avoidFerries: false,
    avoidMotorways: false,
    avoidUnpaved: false,
    alternatives: false,
    vehicleType: 'hgv',
    avoidUrbanAreas: [],
    // Opt-in ESTRITO: nasce desligado. O RouteForm liga-o quando o perfil do
    // cliente tem useTachograph===true (e o veículo não é ligeiro). Antes
    // nascia `true`, o que punha o tacógrafo activo em clientes que o têm
    // desligado no Perfil (ex.: Move Express) sempre que a rota era reposta.
    useTachograph: false,
    truckWeight: '',
    truckAxleLoad: '',
    truckHeight: '',
    truckWidth: '',
    truckLength: '',
    currency: 'EUR',
  },
  alternatives: [],
  selectedAlternative: 0,
  roadBreakdown: null,
  routeSteps: [],
  pois: [],
  poisLoading: false,
  trafficIncidents: [],
  trafficLoading: false,
  customCosts: [],    // [{ id, name, value }]
  estimatedCost: null, // operator's predicted cost for the trip (€)
  actualCost: null,    // actual cost after completion (€)
  sentToVehicle: false,
  sentToVehicleAt: null,
  sourceOTId: null,   // ID of the OT that created this route (for auto-association)
  sourceOTIds: null,  // Array of OT IDs when the route consolidates several OTs
  modifiedSinceLoad: false, // dirty flag — true when user edited after loading a dispatched route
  readOnly: false,          // when true, the route is in view-only mode (no edits allowed)
  // Re-plan mode: when set to a route id, the planner shows a prominent
  // "Guardar & Reenviar" button that updates the route AND re-dispatches to drivers.
  // Used for editing an in-progress route and notifying the driver of changes.
  replanningRouteId: null,
  // OT id to reopen the live panel for once the operator finishes the replan
  // (whether they save, save+resend, or cancel). Set when the replan was
  // launched from the Live panel; OrdersView consumes it after navigating back.
  replanReturnLiveOTId: null,
};

// Fetch saved routes from API
export const fetchSavedRoutes = createAsyncThunk("routes/fetchSaved", async (token) => {
  const res = await fetch(`${API_BASE}/routes`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch routes");
  return res.json();
});

// Paginated fetch with filters
export const fetchSavedRoutesPaginated = createAsyncThunk(
  "routes/fetchSavedPaginated",
  async ({ token, page = 1, limit = 15, filters = {}, sort = {} }) => {
    const params = new URLSearchParams({ page, limit });
    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.driverId) params.set('driverId', filters.driverId);
    if (filters.vehicleId) params.set('vehicleId', filters.vehicleId);
    if (filters.minDistance) params.set('minDistance', filters.minDistance);
    if (filters.maxDistance) params.set('maxDistance', filters.maxDistance);
    if (sort.field) params.set('sortBy', sort.field);
    if (sort.dir) params.set('sortDir', sort.dir);
    const res = await fetch(`${API_BASE}/routes?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to fetch routes");
    return res.json();
  }
);

// Bulk delete routes
export const bulkDeleteRoutes = createAsyncThunk(
  "routes/bulkDelete",
  async ({ token, routeIds }) => {
    const res = await fetch(`${API_BASE}/routes/bulk-delete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ routeIds }),
    });
    if (!res.ok) throw new Error("Failed to delete routes");
    return { routeIds };
  }
);

// Bulk update status
export const bulkUpdateStatus = createAsyncThunk(
  "routes/bulkStatus",
  async ({ token, routeIds, status }) => {
    const res = await fetch(`${API_BASE}/routes/bulk-status`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ routeIds, status }),
    });
    if (!res.ok) throw new Error("Failed to update routes");
    return res.json();
  }
);

// Fetch dispatch history for a route
export const fetchDispatchHistory = createAsyncThunk(
  "routes/fetchDispatchHistory",
  async ({ token, routeId }) => {
    const res = await fetch(`${API_BASE}/routes/${routeId}/dispatch-history`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to fetch dispatch history");
    return res.json();
  }
);

// Fetch single route with full routeData (for loading from paginated list)
export const fetchRouteDetail = createAsyncThunk(
  "routes/fetchDetail",
  async ({ token, routeId }) => {
    const res = await fetch(`${API_BASE}/routes/${routeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to fetch route detail");
    const route = await res.json();
    // Estado das OT da rota — usado para marcar a verde as paragens já
    // entregues no painel e no mapa. Best-effort: se falhar, a rota
    // carrega na mesma sem a indicação de entrega.
    try {
      const otRes = await fetch(
        `${API_BASE}/transport-orders?assignedRouteId=${routeId}&pageSize=200`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (otRes.ok) {
        const otData = await otRes.json();
        const list = Array.isArray(otData) ? otData : (otData.data || otData.items || []);
        const otStatuses = {};
        list.forEach(o => {
          if (o.id != null) otStatuses[`id:${o.id}`] = o.status;
          if (o.orderNumber) otStatuses[`num:${o.orderNumber}`] = o.status;
        });
        route._otStatuses = otStatuses;
      }
    } catch { /* ignora — sem indicação de entrega */ }
    return route;
  }
);

// Save route to API
// `dispatch` (default true) controls whether linked OTs are flipped to
// in_transit and the driver is notified. When false the route is saved
// but stays in planning mode (OTs remain 'assigned'). Used by the
// "Só gravar" button on SaveRouteDialog.
export const saveRouteToAPI = createAsyncThunk("routes/saveToAPI", async ({ token, route, dispatch: shouldDispatch = true }) => {
  const body = {
    name: route.name || 'Rota sem nome',
    cargoRef: route.cargoRef || null,
    // shouldDispatch=true  → 'planned' so OTs get auto-promoted to
    //                        in_transit by the /assign call below
    //                        (driver gets the route).
    // shouldDispatch=false → 'draft' so OTs stay 'assigned' and the
    //                        driver is NOT notified. Operator chose
    //                        "Só gravar" — pure planning.
    status: shouldDispatch ? 'planned' : 'draft',
    sentToVehicle: shouldDispatch,
    origin: route.origin,
    destination: route.destination,
    waypoints: route.waypoints || [],
    routeData: {
      computedRoute: simplifyComputedRoute(route.computedRoute),
      stops: route.stops,
      alternatives: route.alternatives,
      selectedAlternative: route.selectedAlternative,
      roadBreakdown: route.roadBreakdown,
      routeSteps: route.routeSteps,
      routeOptions: route.routeOptions,
      fuelType: route.fuelType,
      fuelConsumption: route.fuelConsumption,
      fuelPrice: route.fuelPrice,
      routeMarkers: route.routeMarkers || [],
      roundTrip: route.roundTrip || false,
      arrivalTimeWindow: route.arrivalTimeWindow || null,
      customCosts: route.customCosts || [],
      // Provider usado no cálculo (HERE/Google) — persistir para
      // a badge da toolbar ser restaurada ao reabrir a rota.
      provider: route.provider || null,
      vehicleInfo: (route.vehicleId || route.vehiclePlate) ? {
        id: route.vehicleId || null,
        plate: route.vehiclePlate || '',
        type: route.vehicleType || '',
      } : null,
      driverInfo: (route.driverId || route.driverName) ? {
        id: route.driverId || null,
        name: route.driverName || '',
      } : null,
    },
    // Send vehicleId/driverId — backend now upserts a shadow row in
    // `vehicles` keyed by (clientId, externalId) before saving the FK,
    // so the previous workaround (storing only in routeData.vehicleInfo)
    // is no longer needed. Without these the live monitor can't resolve
    // the Traccar deviceId and the GPS panel stays "Sem GPS".
    vehicleId: route.vehicleId || null,
    vehiclePlate: route.vehiclePlate || null,
    driverId: route.driverId || null,
    departureTime: route.departureTime || null,
    totalDistanceM: route.computedRoute?.distance || null,
    totalDurationS: route.computedRoute?.duration || null,
    // Operator-entered transport costs (estimated before dispatch, actual after completion)
    estimatedCost: route.estimatedCost != null && route.estimatedCost !== '' ? Number(route.estimatedCost) : null,
    actualCost: route.actualCost != null && route.actualCost !== '' ? Number(route.actualCost) : null,
    // Histórico de OTs que originaram a rota — backend persiste em
    // routes.source_ot_ids (coluna INTEGER[]). Garante que o contexto
    // OT-derived é preservado entre sessões. Composição:
    //   • sourceOTIds (array)  → consolidação multi-OT
    //   • sourceOTId (single)  → OT single
    //   • ambos vazios         → criação manual no planeamento
    sourceOtIds: (() => {
      if (Array.isArray(route.sourceOTIds) && route.sourceOTIds.length > 0) {
        return route.sourceOTIds.map(n => parseInt(n)).filter(Number.isFinite);
      }
      if (route.sourceOTId) return [parseInt(route.sourceOTId)].filter(Number.isFinite);
      return [];
    })(),
  };

  // If route has a DB id (integer), update; otherwise create
  const isExisting = route.dbId && typeof route.dbId === 'number';
  const url = isExisting ? `${API_BASE}/routes/${route.dbId}` : `${API_BASE}/routes`;
  const method = isExisting ? 'PUT' : 'POST';

  // On UPDATE + "Só gravar": don't downgrade an already-dispatched route
  // back to 'draft'. Strip the status so the backend keeps the current
  // value. We only want to PROMOTE to 'planned' when the operator picks
  // "Gravar e despachar" — never demote silently.
  if (isExisting && !shouldDispatch) {
    delete body.status;
    delete body.sentToVehicle;
  }

  // Comprime o payload com gzip — a geometria da rota pode passar de 1 MB e o
  // UPLOAD em redes lentas (ex.: Angola) era o grande gargalo ao gravar (~5s).
  // O express.json() do backend auto-infla (inflate=true), por isso é
  // transparente. Fallback para JSON simples se o browser não suportar.
  const jsonBody = JSON.stringify(body);
  let fetchBody = jsonBody;
  const extraHeaders = {};
  if (typeof CompressionStream !== 'undefined' && jsonBody.length > 100000) {
    try {
      const gzStream = new Blob([jsonBody]).stream().pipeThrough(new CompressionStream('gzip'));
      fetchBody = await new Response(gzStream).blob();
      extraHeaders['Content-Encoding'] = 'gzip';
    } catch { fetchBody = jsonBody; }
  }
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...extraHeaders,
    },
    body: fetchBody,
  });
  if (!res.ok) {
    // Propaga a mensagem real do backend (ex.: geometria inconsistente,
    // rota terminal) para o toast — antes o erro era genérico e o save
    // falhava em silêncio ("não faz nada").
    let msg = 'Falha ao gravar a rota';
    try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* sem corpo */ }
    throw new Error(msg);
  }
  const saved = await res.json();

  // Auto-associate TODAS as OT da rota — não só as de origem (sourceOTIds), mas
  // também as referenciadas nos waypoints/origem/destino. Sem isto, as OT
  // ADICIONADAS a uma rota já existente ficavam sem viatura/motorista/enviado
  // (só a OT original era atribuída).
  const otIdSet = new Set();
  const addOt = (v) => { const n = parseInt(v); if (Number.isFinite(n)) otIdSet.add(n); };
  (Array.isArray(route.sourceOTIds) ? route.sourceOTIds : []).forEach(addOt);
  if (route.sourceOTId) addOt(route.sourceOTId);
  const collectOt = (p) => { if (p && p.otId != null) addOt(p.otId); };
  (Array.isArray(route.waypoints) ? route.waypoints : []).forEach(collectOt);
  collectOt(route.origin); collectOt(route.destination);
  const otIds = [...otIdSet];
  if (otIds.length > 0 && saved.id) {
    await Promise.all(otIds.map(otId =>
      fetch(`${API_BASE}/transport-orders/${otId}/assign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ routeId: saved.id }),
      }).catch(() => { /* best-effort per OT */ })
    ));
  }

  return saved;
});

// Send route to vehicle — saves route first if needed, then marks as sent
export const sendRouteToVehicle = createAsyncThunk("routes/sendToVehicle", async ({ token, route, vehicleId }) => {
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  let dbId = route.dbId;

  // Step 1: If route not saved yet, create it first
  if (!dbId) {
    const body = {
      name: route.name || 'Rota sem nome',
      cargoRef: route.cargoRef || null,
      status: 'draft',
      origin: route.origin,
      destination: route.destination,
      waypoints: route.waypoints || [],
      routeData: {
        computedRoute: simplifyComputedRoute(route.computedRoute),
        stops: route.stops,
        alternatives: route.alternatives,
        selectedAlternative: route.selectedAlternative,
        roadBreakdown: route.roadBreakdown,
        routeSteps: route.routeSteps,
        routeOptions: route.routeOptions,
        fuelType: route.fuelType,
        fuelConsumption: route.fuelConsumption,
        fuelPrice: route.fuelPrice,
        pois: route.pois,
        routeMarkers: route.routeMarkers || [],
        roundTrip: route.roundTrip || false,
        arrivalTimeWindow: route.arrivalTimeWindow || null,
        customCosts: route.customCosts || [],
        vehicleInfo: route.vehicleId ? {
          id: route.vehicleId,
          plate: route.vehiclePlate || '',
          type: route.vehicleType || '',
        } : null,
        driverInfo: route.driverId ? {
          id: route.driverId,
          name: route.driverName || '',
        } : null,
      },
      departureTime: route.departureTime || null,
      totalDistanceM: route.computedRoute?.distance || null,
      totalDurationS: route.computedRoute?.duration || null,
      sentToVehicle: true,
    };

    const createRes = await fetch(`${API_BASE}/routes`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!createRes.ok) throw new Error("Failed to save route");
    const created = await createRes.json();
    return created;
  }

  // Step 2: Route already exists — call send endpoint with vehicleId
  const sendRes = await fetch(`${API_BASE}/routes/${dbId}/send`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ vehicleId: vehicleId || null }),
  });
  if (!sendRes.ok) throw new Error("Failed to send route to vehicle");
  return sendRes.json();
});

// Dispatch routes to drivers in batch
export const dispatchRoutes = createAsyncThunk("routes/dispatch", async ({ token, assignments }) => {
  const res = await fetch(`${API_BASE}/routes/dispatch`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ assignments }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Erro ao despachar rotas");
  }
  return res.json();
});

// Update route execution status (planned → in_progress → completed / cancelled)
export const updateRouteStatus = createAsyncThunk("routes/updateStatus", async ({ token, routeId, status }) => {
  const res = await fetch(`${API_BASE}/routes/${routeId}/status`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error("Erro ao atualizar estado da rota");
  return res.json();
});

// Reabrir uma rota terminal (completed/cancelled) → volta a editável (planned)
export const reopenRoute = createAsyncThunk("routes/reopen", async ({ token, routeId }, { rejectWithValue }) => {
  const res = await fetch(`${API_BASE}/routes/${routeId}/reopen`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    return rejectWithValue(e.error || 'Erro ao reabrir rota');
  }
  return res.json();
});

// Reutilizar — duplica uma rota como nova draft (sem ligações às OTs originais)
export const duplicateRoute = createAsyncThunk("routes/duplicate", async ({ token, routeId }, { rejectWithValue }) => {
  const res = await fetch(`${API_BASE}/routes/${routeId}/duplicate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    return rejectWithValue(e.error || 'Erro ao duplicar rota');
  }
  return res.json();
});

// Fetch actual GPS trail from Traccar for a dispatched route
export const fetchActualTrail = createAsyncThunk("routes/fetchActualTrail", async ({ token, routeId }) => {
  const res = await fetch(`${API_BASE}/routes/${routeId}/actual-trail`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Erro ao obter rota real");
  }
  const data = await res.json();
  // Limpa fixes lixo do Traccar (0,0, saltos absurdos) — senão a
  // distância real e o desvio sairiam inflados em milhares de km.
  const raw = (data?.trail || data?.points || []).map(p => ({
    lat: Number(p.lat), lng: Number(p.lng),
    speed: p.speed, timestamp: p.timestamp || p.fixTime,
  }));
  return { ...data, trail: cleanTrail(raw) };
});

// Delete route from API
export const deleteRouteFromAPI = createAsyncThunk("routes/deleteFromAPI", async ({ token, routeId, force = false, purgeHistory = false }, { rejectWithValue }) => {
  const params = [];
  if (force) params.push('force=true');
  if (purgeHistory) params.push('purgeHistory=true');
  const qs = params.length ? `?${params.join('&')}` : '';
  const res = await fetch(`${API_BASE}/routes/${routeId}${qs}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 409) {
    // Linked OTs — surface them so the UI can prompt the operator. The server
    // distinguishes between "open OTs linked" (plain 409, needs force) and
    // "closed OTs linked" (409 + `needsPurgeHistory`, needs purgeHistory).
    const data = await res.json().catch(() => ({}));
    return rejectWithValue({
      code: data.needsPurgeHistory ? 'linked-closed' : 'linked',
      linkedOTs: data.linkedOTs || [],
      message: data.message,
    });
  }
  if (!res.ok) throw new Error("Failed to delete route");
  return routeId;
});

// Convert API route to frontend format
function apiRouteToFrontend(apiRoute) {
  const rd = apiRoute.routeData || {};
  // Enriquece cada paragem com o estado da OT (ex.: 'delivered'), para o
  // painel e o mapa poderem marcar a verde as paragens já entregues.
  const otStatuses = apiRoute._otStatuses || null;
  const otStatusFor = (wp) => {
    if (!otStatuses || !wp) return undefined;
    return otStatuses[`id:${wp.otId}`] ?? otStatuses[`num:${wp.orderNumber}`];
  };
  const enrich = (wp) => (wp ? { ...wp, otStatus: otStatusFor(wp) } : wp);
  return {
    id: `api-${apiRoute.id}`,
    dbId: apiRoute.id,
    name: apiRoute.name || '',
    origin: apiRoute.origin,
    destination: enrich(apiRoute.destination),
    waypoints: (apiRoute.waypoints || []).map(enrich),
    computedRoute: rd.computedRoute || null,
    stops: rd.stops || [],
    status: rd.computedRoute ? 'ready' : 'idle',
    vehicleId: apiRoute.vehicleId ?? rd.vehicleInfo?.id ?? '',
    driverId: apiRoute.driverId ?? rd.driverInfo?.id ?? '',
    vehiclePlate: apiRoute.vehicle?.plate || rd.vehicleInfo?.plate || '',
    vehicleType: apiRoute.vehicle?.type || rd.vehicleInfo?.type || '',
    driverName: apiRoute.driver?.name || rd.driverInfo?.name || '',
    cargoRef: apiRoute.cargoRef || '',
    departureTime: apiRoute.departureTime || null,
    arrivalTimeWindow: rd.arrivalTimeWindow || null,
    errorMessage: '',
    warning: '',
    fuelType: rd.fuelType || 'diesel',
    fuelConsumption: rd.fuelConsumption || null,
    fuelPrice: rd.fuelPrice || null,
    routeOptions: rd.routeOptions || emptyRoute.routeOptions,
    alternatives: rd.alternatives || [],
    selectedAlternative: rd.selectedAlternative || 0,
    roadBreakdown: rd.roadBreakdown || null,
    routeSteps: rd.routeSteps || [],
    pois: rd.pois || [],
    poisLoading: false,
    routeMarkers: rd.routeMarkers || [],
    roundTrip: rd.roundTrip || false,
    customCosts: rd.customCosts || [],
    estimatedCost: apiRoute.estimatedCost != null ? Number(apiRoute.estimatedCost) : null,
    actualCost: apiRoute.actualCost != null ? Number(apiRoute.actualCost) : null,
    sentToVehicle: apiRoute.sentToVehicle || false,
    sentToVehicleAt: apiRoute.sentToVehicleAt || null,
    routeDrivers: (apiRoute.routeDrivers || []).map(rd => ({
      id: rd.id,
      driverId: rd.driver?.id || rd.driverId,
      driverName: rd.driver?.name || '',
      externalId: rd.driver?.externalId || null,
      status: rd.status,
      assignedAt: rd.assignedAt,
    })),
    savedAt: apiRoute.updatedAt || apiRoute.createdAt,
    dbStatus: apiRoute.status || 'draft',
    // Provider usado no cálculo da rota (HERE/Google) — alimenta o
    // badge "H HERE" / "G Google" na toolbar. Rotas antigas (sem este
    // campo persistido) ficam sem badge ao reabrir.
    provider: rd.provider || null,
    actualStartedAt: rd.actualStartedAt || null,
    actualCompletedAt: rd.actualCompletedAt || null,
    actualCancelledAt: rd.actualCancelledAt || null,
    // OTs que originaram esta rota — persistido na BD desde v11.2.
    // Restaura o contexto OT-derived ao reabrir uma rota, garantindo
    // que chips de armazém/motorista, escondimento do checkbox
    // "Ida e volta", badges de tipo de paragem, etc., funcionam
    // coerentemente após reload.
    sourceOTIds: Array.isArray(apiRoute.sourceOtIds) && apiRoute.sourceOtIds.length > 0
      ? apiRoute.sourceOtIds
      : null,
    sourceOTId: Array.isArray(apiRoute.sourceOtIds) && apiRoute.sourceOtIds.length === 1
      ? apiRoute.sourceOtIds[0]
      : null,
  };
}

const routesSlice = createSlice({
  name: 'routes',
  initialState: {
    current: { ...emptyRoute, id: 'route-1' },
    previewRoute: null, // temporary route data shown in RouteDetails during compare hover
    routeHistory: [], // snapshots for undo (max 10)
    saved: [],
    savedLoading: false,
    saving: false,
    // Paginated saved routes
    savedPaginated: [],
    savedPagination: { page: 1, limit: 15, total: 0, totalPages: 0 },
    savedFilters: { search: '', status: '', dateFrom: '', dateTo: '', driverId: '', vehicleId: '' },
    savedSort: { field: 'createdAt', dir: 'desc' },
    savedPaginatedLoading: false,
    actualTrail: null,       // { trail: [{lat,lng,speed,timestamp}], deviceId, from, to } or null
    actualTrailLoading: false,
    actualTrailError: null,
  },
  reducers: {
    // Save a snapshot of the current route before modifying points
    _pushHistory: (state) => {
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
    },
    undoRoute: (state) => {
      if (state.routeHistory.length > 0) {
        state.current = state.routeHistory.pop();
      }
    },
    setOrigin: (state, action) => {
      // Only snapshot if route has computed data (meaningful state to undo to)
      if (state.current.computedRoute) {
        state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
        if (state.routeHistory.length > 10) state.routeHistory.shift();
      }
      state.current.origin = action.payload;
      state.current.status = 'idle';
      state.current.computedRoute = null;
      state.current.stops = [];
      state.current.pois = [];
      state.current.poisLoading = false;
      state.current.modifiedSinceLoad = true;
    },
    setDestination: (state, action) => {
      if (state.current.computedRoute) {
        state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
        if (state.routeHistory.length > 10) state.routeHistory.shift();
      }
      state.current.destination = action.payload;
      state.current.status = 'idle';
      state.current.computedRoute = null;
      state.current.stops = [];
      state.current.pois = [];
      state.current.poisLoading = false;
      state.current.modifiedSinceLoad = true;
    },
    addWaypoint: (state, action) => {
      const payload = action.payload || {};
      const list = state.current.waypoints;
      const last = list[list.length - 1];
      const payloadEmpty = !payload.coordinates && !payload.name;
      const lastEmpty = last && !last.coordinates && !last.name;
      // If both tail and incoming are empty, it's a duplicate — drop it.
      if (payloadEmpty && lastEmpty) return;
      // If incoming has data and the tail is empty, fill the tail instead of appending.
      if (!payloadEmpty && lastEmpty) {
        state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
        if (state.routeHistory.length > 10) state.routeHistory.shift();
        list[list.length - 1] = payload;
        state.current.status = 'idle';
        state.current.modifiedSinceLoad = true;
        return;
      }
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      list.push(payload);
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    insertWaypointAt: (state, action) => {
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      const { index, waypoint } = action.payload;
      state.current.waypoints.splice(index, 0, waypoint);
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    updateWaypoint: (state, action) => {
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      const { index, waypoint } = action.payload;
      state.current.waypoints[index] = waypoint;
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    // Atualiza campos do DESTINO sem limpar a geometria calculada (ao contrário
    // de setDestination). Usado p.ex. para o custo editável por ponto (mercadoria)
    // — mudar o custo não deve invalidar a rota no mapa.
    patchDestination: (state, action) => {
      if (!state.current.destination) return;
      state.current.destination = { ...state.current.destination, ...(action.payload || {}) };
      state.current.modifiedSinceLoad = true;
    },
    removeWaypoint: (state, action) => {
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      state.current.waypoints.splice(action.payload, 1);
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    reorderWaypoints: (state, action) => {
      const { fromIndex, toIndex } = action.payload;
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      const wps = state.current.waypoints;
      const [moved] = wps.splice(fromIndex, 1);
      wps.splice(toIndex, 0, moved);
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    // Substitui o array de waypoints de forma atómica. Usado pela
    // re-otimização de rota em curso, que exclui as paragens já entregues
    // (sem isto teríamos de remover índice a índice, com risco de desalinhar).
    setWaypoints: (state, action) => {
      state.routeHistory.push(JSON.parse(JSON.stringify(state.current)));
      if (state.routeHistory.length > 10) state.routeHistory.shift();
      state.current.waypoints = Array.isArray(action.payload) ? action.payload : [];
      state.current.status = 'idle';
      state.current.modifiedSinceLoad = true;
    },
    setRouteName: (state, action) => {
      state.current.name = action.payload;
    },
    setPreviewRoute: (state, action) => {
      state.previewRoute = action.payload;
    },
    setCargoRef: (state, action) => {
      state.current.cargoRef = action.payload;
    },
    setSourceOTId: (state, action) => {
      state.current.sourceOTId = action.payload;
    },
    setSourceOTIds: (state, action) => {
      state.current.sourceOTIds = Array.isArray(action.payload) ? action.payload : null;
    },
    setEstimatedCost: (state, action) => {
      state.current.estimatedCost = action.payload;
      state.current.modifiedSinceLoad = true;
    },
    setActualCost: (state, action) => {
      state.current.actualCost = action.payload;
      state.current.modifiedSinceLoad = true;
    },
    setReplanningRouteId: (state, action) => {
      state.current.replanningRouteId = action.payload || null;
    },
    setReplanReturnLiveOTId: (state, action) => {
      state.current.replanReturnLiveOTId = action.payload || null;
    },
    setReadOnly: (state, action) => {
      state.current.readOnly = !!action.payload;
    },
    setDepartureTime: (state, action) => {
      state.current.departureTime = action.payload;
    },
    setArrivalTimeWindow: (state, action) => {
      state.current.arrivalTimeWindow = action.payload;
    },
    setRoundTrip: (state, action) => {
      state.current.roundTrip = action.payload;
    },
    addRouteMarker: (state, action) => {
      state.current.routeMarkers.push(action.payload);
    },
    updateRouteMarker: (state, action) => {
      const { id, ...updates } = action.payload;
      const marker = state.current.routeMarkers.find(m => m.id === id);
      if (marker) Object.assign(marker, updates);
    },
    removeRouteMarker: (state, action) => {
      state.current.routeMarkers = state.current.routeMarkers.filter(m => m.id !== action.payload);
    },
    setVehicle: (state, action) => {
      if (typeof action.payload === 'object' && action.payload !== null) {
        state.current.vehicleId = action.payload.id || '';
        state.current.vehiclePlate = action.payload.plate || '';
        state.current.vehicleType = action.payload.type || '';
      } else {
        state.current.vehicleId = action.payload;
        state.current.vehiclePlate = '';
        state.current.vehicleType = '';
      }
    },
    setRouteDbId: (state, action) => {
      // Re-associa a rota actual a um registo já existente (por id) para que
      // "Só Gravar" faça UPDATE (PUT) em vez de criar rota nova. Usado no
      // re-plano de OT de combustível — 1 OT ↔ 1 rota, sem órfãs.
      state.current.dbId = action.payload || null;
    },
    setRouteCisterns: (state, action) => {
      // Cisternas atreladas (até 2) da OT de combustível — usadas no resumo
      // da rota (confirmação "rota gravada") e display no mapa. Array de
      // { vehicleId, plate, capacityM3 }. [] limpa.
      state.current.cisterns = Array.isArray(action.payload) ? action.payload : [];
    },
    setDriver: (state, action) => {
      if (typeof action.payload === 'object' && action.payload !== null) {
        state.current.driverId = action.payload.id || '';
        state.current.driverName = action.payload.name || '';
      } else {
        state.current.driverId = action.payload;
        state.current.driverName = '';
      }
    },
    setFuelType: (state, action) => {
      state.current.fuelType = action.payload;
    },
    setFuelConsumption: (state, action) => {
      state.current.fuelConsumption = action.payload;
    },
    setFuelPrice: (state, action) => {
      state.current.fuelPrice = action.payload;
    },
    setCalculating: (state) => {
      state.current.status = 'calculating';
      state.current.errorMessage = '';
      state.current.warning = '';
    },
    setRouteOptions: (state, action) => {
      state.current.routeOptions = { ...state.current.routeOptions, ...action.payload };
    },
    setRouteResult: (state, action) => {
      const { computedRoute, stops, alternatives = [], warning = '', provider = null } = action.payload;
      state.current.computedRoute = computedRoute;
      state.current.stops = stops;
      state.current.alternatives = alternatives;
      state.current.selectedAlternative = 0;
      state.current.roadBreakdown = computedRoute?.roadBreakdown || null;
      state.current.routeSteps = computedRoute?.routeSteps || [];
      state.current.status = 'ready';
      state.current.warning = warning || '';
      state.current.provider = provider || null;
      // Após cálculo bem-sucedido, os waypoints e o computedRoute estão
      // por definição em sincronia. Limpa o dirty flag para o banner
      // "A rota foi alterada" desaparecer. Só volta a true se o
      // operador mexer em algo depois deste momento.
      state.current.modifiedSinceLoad = false;
    },
    selectAlternative: (state, action) => {
      const idx = action.payload;
      const allRoutes = [state.current.computedRoute, ...state.current.alternatives];
      if (allRoutes[idx]) {
        state.current.selectedAlternative = idx;
        state.current.computedRoute = allRoutes[idx];
        const remaining = allRoutes.filter((_, i) => i !== idx);
        state.current.alternatives = remaining;
      }
    },
    setRouteError: (state, action) => {
      state.current.status = 'error';
      state.current.errorMessage = action.payload;
      state.current.computedRoute = null;
      state.current.stops = [];
    },
    clearRoute: (state) => {
      // Preserve replanReturnLiveOTId across clearRoute — the cancel/save flows
      // dispatch clearRoute() before navigating, but OrdersView only consumes
      // the id AFTER the navigation lands. Without this, the id is wiped before
      // OrdersView reads it and the live panel never reopens.
      const keepReturnId = state.current.replanReturnLiveOTId;
      state.current = { ...emptyRoute, id: state.current.id, replanReturnLiveOTId: keepReturnId };
    },
    // Kept for backwards compat - now triggers async save
    saveCurrentRoute: (state) => {
      // Mark as saving (actual save happens via saveRouteToAPI thunk)
      state.saving = true;
    },
    loadRoute: (state, action) => {
      const route = state.saved.find(r => r.id === action.payload)
        || state.savedPaginated.find(r => r.id === action.payload);
      if (route) {
        state.current = { ...route };
      }
    },
    importRoutes: (state, action) => {
      const incoming = Array.isArray(action.payload) ? action.payload : [action.payload];
      incoming.forEach(route => {
        const idExists = state.saved.some(r => r.id === route.id);
        const newRoute = idExists
          ? { ...route, id: `route-${Date.now()}-${Math.random().toString(36).slice(2)}` }
          : route;
        state.saved.push({ ...newRoute, savedAt: newRoute.savedAt || new Date().toISOString() });
      });
    },
    setSavedFilters: (state, action) => {
      state.savedFilters = { ...state.savedFilters, ...action.payload };
      state.savedPagination.page = 1; // reset to page 1 on filter change
    },
    setSavedSort: (state, action) => {
      state.savedSort = action.payload;
      state.savedPagination.page = 1;
    },
    setSavedPage: (state, action) => {
      state.savedPagination.page = action.payload;
    },
    setPois: (state, action) => {
      state.current.pois = action.payload;
    },
    setPoisLoading: (state, action) => {
      state.current.poisLoading = action.payload;
    },
    setTrafficIncidents: (state, action) => {
      state.current.trafficIncidents = action.payload;
    },
    setTrafficLoading: (state, action) => {
      state.current.trafficLoading = action.payload;
    },
    clearActualTrail: (state) => {
      state.actualTrail = null;
      state.actualTrailLoading = false;
      state.actualTrailError = null;
    },
    updateStopPosition: (state, action) => {
      const { index, progressRatio, km, coordinates } = action.payload;
      if (state.current.stops[index]) {
        state.current.stops[index].progressRatio = progressRatio;
        state.current.stops[index].km = km;
        if (coordinates) state.current.stops[index].coordinates = coordinates;
      }
    },
    addCustomCost: (state, action) => {
      const { name, value } = action.payload;
      state.current.customCosts.push({ id: Date.now(), name, value: Number(value) || 0 });
    },
    removeCustomCost: (state, action) => {
      state.current.customCosts = state.current.customCosts.filter(c => c.id !== action.payload);
    },
    updateCustomCost: (state, action) => {
      const { id, name, value } = action.payload;
      const cost = state.current.customCosts.find(c => c.id === id);
      if (cost) {
        if (name !== undefined) cost.name = name;
        if (value !== undefined) cost.value = Number(value) || 0;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch saved routes
      .addCase(fetchSavedRoutes.pending, (state) => {
        state.savedLoading = true;
      })
      .addCase(fetchSavedRoutes.fulfilled, (state, action) => {
        state.saved = action.payload.map(apiRouteToFrontend);
        state.savedLoading = false;
      })
      .addCase(fetchSavedRoutes.rejected, (state) => {
        state.savedLoading = false;
      })
      // Save route
      .addCase(saveRouteToAPI.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveRouteToAPI.fulfilled, (state, action) => {
        state.saving = false;
        const saved = apiRouteToFrontend(action.payload);
        const idx = state.saved.findIndex(r => r.dbId === saved.dbId);
        if (idx >= 0) {
          state.saved[idx] = saved;
        } else {
          state.saved.push(saved);
        }
        // Update current route with dbId, name, and driver/vehicle
        state.current.dbId = saved.dbId;
        state.current.id = saved.id;
        state.current.name = saved.name || state.current.name;
        state.current.driverId = saved.driverId || '';
        state.current.driverName = saved.driverName || '';
        state.current.vehicleId = saved.vehicleId || '';
        state.current.vehiclePlate = saved.vehiclePlate || '';
        state.current.vehicleType = saved.vehicleType || '';
        // Route was persisted — reset dirty flag
        state.current.modifiedSinceLoad = false;
      })
      .addCase(saveRouteToAPI.rejected, (state) => {
        state.saving = false;
      })
      // Send route to vehicle
      .addCase(sendRouteToVehicle.pending, (state) => {
        state.saving = true;
      })
      .addCase(sendRouteToVehicle.fulfilled, (state, action) => {
        state.saving = false;
        const apiRoute = action.payload;
        const saved = apiRouteToFrontend(apiRoute);
        saved.sentToVehicle = true;
        saved.sentToVehicleAt = apiRoute.sentToVehicleAt;
        const idx = state.saved.findIndex(r => r.dbId === saved.dbId);
        if (idx >= 0) {
          state.saved[idx] = saved;
        } else {
          state.saved.push(saved);
        }
        state.current.dbId = saved.dbId;
        state.current.id = saved.id;
        state.current.sentToVehicle = true;
      })
      .addCase(sendRouteToVehicle.rejected, (state) => {
        state.saving = false;
      })
      // Delete route
      .addCase(deleteRouteFromAPI.fulfilled, (state, action) => {
        state.saved = state.saved.filter(r => r.dbId !== action.payload);
      })
      // Dispatch routes to drivers
      .addCase(dispatchRoutes.fulfilled, (state, action) => {
        const updated = action.payload.map(apiRouteToFrontend);
        for (const u of updated) {
          const idx = state.saved.findIndex(r => r.dbId === u.dbId);
          if (idx >= 0) state.saved[idx] = u;
          // Also update current route if it matches
          if (state.current.dbId === u.dbId) {
            state.current.routeDrivers = u.routeDrivers;
          }
        }
      })
      // Update route status
      .addCase(updateRouteStatus.fulfilled, (state, action) => {
        const saved = apiRouteToFrontend(action.payload);
        const idx = state.saved.findIndex(r => r.dbId === saved.dbId);
        if (idx >= 0) state.saved[idx] = { ...state.saved[idx], ...saved };
      })
      // Fetch single route detail → load as current (fresh from DB → not dirty)
      .addCase(fetchRouteDetail.fulfilled, (state, action) => {
        const route = apiRouteToFrontend(action.payload);
        // A route is editable until it reaches a terminal state. Operators
        // need to tweak routes mid-execution (extra OT, route adjustment)
        // so we only lock the planner when the route is `completed` or
        // `cancelled`. The explicit "Editar Rota" / replan flow always
        // clears readOnly regardless.
        const status = action.payload.status;
        const isTerminal = status === 'completed' || status === 'cancelled';
        // BUT — if this fetch was triggered AS PART of the replan flow (the
        // replanningRouteId was set just before), that's an explicit edit
        // intent and we keep edits enabled.
        const isReplanning = state.current.replanningRouteId
          && Number(state.current.replanningRouteId) === Number(action.payload.id);
        // Caller may also have explicitly forced readOnly already.
        const explicitReadOnly = state.current.readOnly;
        const readOnly = isReplanning ? false : (explicitReadOnly || isTerminal);
        // Preserve replan markers across the state replacement — without these,
        // the "Guardar e Reenviar" bar in the planner won't appear because the
        // bar conditionally renders on `replanningRouteId`. The Editar-Rota and
        // recalc-on-start flows both rely on these surviving the fetch.
        const keepReplanningId = state.current.replanningRouteId;
        const keepReturnOTId = state.current.replanReturnLiveOTId;
        state.current = {
          ...route,
          modifiedSinceLoad: false,
          readOnly,
          replanningRouteId: keepReplanningId,
          replanReturnLiveOTId: keepReturnOTId,
        };
      })
      // Paginated routes
      .addCase(fetchSavedRoutesPaginated.pending, (state) => {
        state.savedPaginatedLoading = true;
      })
      .addCase(fetchSavedRoutesPaginated.fulfilled, (state, action) => {
        state.savedPaginated = (action.payload.data || []).map(apiRouteToFrontend);
        state.savedPagination = action.payload.pagination || state.savedPagination;
        state.savedPaginatedLoading = false;
      })
      .addCase(fetchSavedRoutesPaginated.rejected, (state) => {
        state.savedPaginatedLoading = false;
      })
      // Bulk delete
      .addCase(bulkDeleteRoutes.fulfilled, (state, action) => {
        const ids = action.payload.routeIds;
        state.saved = state.saved.filter(r => !ids.includes(r.dbId));
        state.savedPaginated = state.savedPaginated.filter(r => !ids.includes(r.dbId));
      })
      // Actual trail
      .addCase(fetchActualTrail.pending, (state) => {
        state.actualTrailLoading = true;
        state.actualTrailError = null;
      })
      .addCase(fetchActualTrail.fulfilled, (state, action) => {
        state.actualTrail = action.payload;
        state.actualTrailLoading = false;
      })
      .addCase(fetchActualTrail.rejected, (state, action) => {
        state.actualTrailLoading = false;
        state.actualTrailError = action.error?.message || 'Erro';
      })
      // Bulk status
      .addCase(bulkUpdateStatus.fulfilled, (state, action) => {
        const updated = (action.payload.updated || []);
        for (const u of updated) {
          const idx = state.saved.findIndex(r => r.dbId === u.id);
          if (idx >= 0) state.saved[idx].dbStatus = u.status;
          const idx2 = state.savedPaginated.findIndex(r => r.dbId === u.id);
          if (idx2 >= 0) state.savedPaginated[idx2].dbStatus = u.status;
        }
      });
  },
});

export const {
  undoRoute,
  setOrigin, setDestination, patchDestination, addWaypoint, insertWaypointAt, updateWaypoint, removeWaypoint, reorderWaypoints, setWaypoints,
  setRouteName, setCargoRef, setSourceOTId, setSourceOTIds, setDepartureTime, setArrivalTimeWindow, setRoundTrip,
  setEstimatedCost, setActualCost, setReadOnly, setReplanningRouteId, setReplanReturnLiveOTId,
  addRouteMarker, updateRouteMarker, removeRouteMarker,
  setVehicle, setDriver, setRouteCisterns, setRouteDbId,
  setFuelType, setFuelConsumption, setFuelPrice,
  setRouteOptions, selectAlternative,
  setCalculating, setRouteResult, setRouteError, clearRoute,
  saveCurrentRoute, loadRoute, importRoutes,
  updateStopPosition, setPois, setPoisLoading, setTrafficIncidents, setTrafficLoading,
  setPreviewRoute,
  setSavedFilters, setSavedSort, setSavedPage,
  addCustomCost, removeCustomCost, updateCustomCost,
  clearActualTrail,
} = routesSlice.actions;

export const selectCurrentRoute = state => state.routes.current;
export const selectPreviewRoute = state => state.routes.previewRoute;
export const selectSavedRoutes = state => state.routes.saved;
export const selectSavedLoading = state => state.routes.savedLoading;
export const selectSaving = state => state.routes.saving;
export const selectCanUndo = state => state.routes.routeHistory.length > 0;
export const selectSavedPaginated = state => state.routes.savedPaginated;
export const selectSavedPagination = state => state.routes.savedPagination;
export const selectSavedFilters = state => state.routes.savedFilters;
export const selectSavedSort = state => state.routes.savedSort;
export const selectSavedPaginatedLoading = state => state.routes.savedPaginatedLoading;
export const selectActualTrail = state => state.routes.actualTrail;
export const selectActualTrailLoading = state => state.routes.actualTrailLoading;
export const selectActualTrailError = state => state.routes.actualTrailError;
export default routesSlice.reducer;
